import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { LeftOutlined } from '@ant-design/icons'
import { Col, Empty, Row } from 'antd'

import B2becMatchingProducts from '@/components/B2becMatchingProducts'
import MatchingProductsWithPaging from '@/components/B2becMatchingProductsWithCategory'
import B2becPagination from '@/components/B2becPagination/B2becPagination'
import B2becSorting from '@/components/B2BecSorting'
import { getSortOption } from '@/components/B2BecSorting/sortOptions'
import CustomButton from '@/components/CustomButton'
import NotFound from '@/components/NotFound'
import B2becCardSkeleton from '@/components/Skeletons/B2becCardSkeleton'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectAccessoryProducts,
  selectDetergentProducts,
  selectHasFetchedMatchingProductsError,
  selectIsFetchingMatchingProducts,
  selectMaterialName,
  selectProductsOfAllTypes,
  selectSuitableProducts,
  selectTotalAccessoryProducts,
  selectTotalDetergentProducts,
  selectTotalSuitableProducts,
} from '@/store/selectors/productSelector'
import { productActions } from '@/store/slices/productSlice'
import { useBreadcrumbs, useDocumentTitle } from '@/hooks'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import { PRODUCT_MATCHING_TYPES, SUITABLE_PRODUCTS_PER_PAGE } from '@/libs/constants'
import { PAGE_TYPES } from '@/libs/constants/adobeAnalytics'
import { linkGenerator } from '@/libs/utils/language'

interface ProductMatchingPageProps {
  title: string
  breadcrumbs: {
    title: string
    path: string
    dynamicPath?: boolean
  }[]
  pageId: string
}

function ProductMatchingPage(props: ProductMatchingPageProps) {
  const { title, breadcrumbs, pageId } = props

  const { materialNumber, type } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  useDocumentTitle(title)
  useBreadcrumbs(breadcrumbs, [{ pathData: [materialNumber, type] }])

  const { setPageInfoData } = useAdobeAnalysis()

  const isFetchingMatchingProducts = useAppSelector(
    selectIsFetchingMatchingProducts,
  )
  const materialName = useAppSelector(selectMaterialName)
  const accessoryProducts = useAppSelector(selectAccessoryProducts)
  const detergentProducts = useAppSelector(selectDetergentProducts)
  const suitableProducts = useAppSelector(selectSuitableProducts)
  const totalAccessoryProducts = useAppSelector(selectTotalAccessoryProducts)
  const totalDetergentProducts = useAppSelector(selectTotalDetergentProducts)
  const totalSuitableProducts = useAppSelector(selectTotalSuitableProducts)
  const productsOfAllTypes = useAppSelector(selectProductsOfAllTypes)
  const hasFetchedMatchingProductsError = useAppSelector(
    selectHasFetchedMatchingProductsError,
  )
  const [shouldScrollToTop, setShouldScrollToTop] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortOption, setSortOption] = useState(() => getSortOption('name.desc'))

  const handlePageChange = useCallback((pageNumber) => {
    setShouldScrollToTop(true)
    setCurrentPage(pageNumber)
  }, [])

  const handleSort = useCallback((order) => {
    setSortOption(order)
  }, [])

  const onShowAllHandler = (productType) => {
    navigate(linkGenerator(`/product/${materialNumber}/${productType}`))
  }

  let content = <Empty />
  let header = null

  useLayoutEffect(() => {
    if (shouldScrollToTop) {
      window.scrollTo(0, 0)
    }

    return () => setShouldScrollToTop(false)
  }, [shouldScrollToTop])

  useEffect(() => {
    return () => {
      dispatch(productActions.clearMatchingProductsWhenUnmount())
    }
  }, [dispatch])

  useEffect(() => {
    // check invalid matching type
    if (!Object.values(PRODUCT_MATCHING_TYPES).includes(type)) {
      navigate('/404page')
      return
    }

    dispatch(
      productActions.getMatchingProducts({
        productType: type,
        materialNumber,
        pageNumber: currentPage,
        sortOrder: sortOption.sortOrder,
      }),
    )
  }, [
    type,
    navigate,
    dispatch,
    currentPage,
    materialNumber,
    sortOption.sortOrder,
  ])

  useEffect(() => {
    const matchingType = type === PRODUCT_MATCHING_TYPES.ALL ? 'overview' : type
    setPageInfoData({
      pageName: `products match:${materialNumber}:${matchingType}`,
      pageId,
      pageType: PAGE_TYPES.PRODUCT_MATCHING,
    })
  }, [setPageInfoData, materialNumber, pageId, type])

  if (hasFetchedMatchingProductsError) {
    content = <NotFound />
  }

  const renderProducts = (products = [], totalProducts = 0) => {
    header = (
      <>
        <div className="search-results__header__title">
          {t('searchResultsPage.title', {
            count: totalProducts,
          }).trim()} <q>{materialName}</q>
          {t(`searchResultsPage.header.${type}`)}
        </div>
        <Row>
          <Col lg={12} md={12} sm={12} xl={12} xs={18}>
            <CustomButton
              buttonSize="small"
              onClick={() => {
                navigate(linkGenerator(`/product/${materialNumber}/all`))
              }}
            >
              <LeftOutlined />
              {t('searchResultsPage.button.backToOverview')}
            </CustomButton>
          </Col>
          <Col
            lg={12}
            md={12}
            sm={12}
            style={{ textAlign: 'right' }}
            xl={12}
            xs={6}
          >
            <B2becSorting sortOption={sortOption} onSort={handleSort} />
          </Col>
        </Row>
      </>
    )

    content = (
      <div className="search-results__content">
        {isFetchingMatchingProducts
          ? <B2becCardSkeleton number={SUITABLE_PRODUCTS_PER_PAGE} />
          : <MatchingProductsWithPaging products={products} />}

        <B2becPagination
          hideOnSinglePage
          current={currentPage}
          total={totalProducts}
          onPageChange={handlePageChange}
        />
      </div>
    )
  }

  if (type === PRODUCT_MATCHING_TYPES.ALL) {
    return (
      <div className="container-full-width-page">
        <div className="search-results">
          <div className="search-results__header">
            <div className="search-results__header__title">
              {t('searchResultsPage.title', {
                count: null,
              }).trim()} <q>{materialName}</q>
            </div>
          </div>
          <B2becMatchingProducts
            dataSource={productsOfAllTypes}
            materialNumber={materialNumber}
            onShowAllHandler={onShowAllHandler}
          />
        </div>
      </div>
    )
  }

  if (type === PRODUCT_MATCHING_TYPES.ACCESSORIES) {
    renderProducts(accessoryProducts, totalAccessoryProducts)
  } else if (type === PRODUCT_MATCHING_TYPES.DETERGENTS) {
    renderProducts(detergentProducts, totalDetergentProducts)
  } else if (type === PRODUCT_MATCHING_TYPES.SUITABLE) {
    renderProducts(suitableProducts, totalSuitableProducts)
  }

  return (
    <div className="container-full-width-page">
      <div className="search-results">
        <div className="search-results__header">{header}</div>
        {content}
      </div>
    </div>
  )
}

export default ProductMatchingPage
